import React, { useState, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './ContactSection.css';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Web3Forms configuration — valore da .env (o --build-arg), risolto a build time.
// Chiave pubblica per design: la protezione anti-abuso e' la domain allowlist
// nella dashboard Web3Forms + honeypot `botcheck`.
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

// EmailJS / gwformsend - rimossi, vedi v1.1-bs-backend per implementazioni precedenti

const initialState = {
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
    botcheck: '', // honeypot Web3Forms: deve restare vuoto
};

function ContactSection() {
    const [formData, setFormData] = useState(initialState);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        // Honeypot anti-bot Web3Forms: se compilato, silenzia l'invio
        if (formData.botcheck) {
            return;
        }

        setIsSubmitting(true);

        try {
            const submitFormData = new FormData();
            submitFormData.append('access_key', WEB3FORMS_ACCESS_KEY);
            submitFormData.append('name', formData.name);
            submitFormData.append('email', formData.email);
            submitFormData.append('company', formData.company);
            submitFormData.append('phone', formData.phone);
            submitFormData.append('service', formData.service);
            submitFormData.append('message', formData.message);
            submitFormData.append('botcheck', formData.botcheck);
            submitFormData.append('subject', `Richiesta: ${formData.service || 'Generico'} - ${formData.name}`);
            submitFormData.append('from_name', 'Sito BearSoftware');
            submitFormData.append('replyto', formData.email);

            const response = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                body: submitFormData
            });

            const data = await response.json();

            if (data.success) {
                setAlertType('success');
                setAlertMessage('Messaggio inviato con successo! Ti ricontatteremo il prima possibile.');
                setFormData(initialState);
            } else {
                setAlertType('danger');
                setAlertMessage(data.message || 'Errore durante l\'invio del messaggio. Riprova.');
            }
        } catch (error) {
            console.error('Error:', error);
            setAlertType('danger');
            setAlertMessage('Errore generico. Siamo spiacenti. Riprova più tardi.');
        } finally {
            setIsSubmitting(false);
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 8000);
        }
    }, [formData, isSubmitting]);

    return (
        <section id="contact" className="contact-section section-padding">
            <Container>
                <Row>
                    <Col lg={8} className="mx-auto text-center mb-5">
                        <h2 className="section-title text-white">
                            Iniziamo a <span className="gradient-text-light">Collaborare</span>
                        </h2>
                        <p className="section-subtitle text-white-50">
                            Hai un progetto in mente? Contattaci per una consulenza gratuita
                        </p>
                    </Col>
                </Row>

                <Row>
                    <Col lg={6} className="mb-5">
                        {showAlert && (
                            <Alert variant={alertType} className="mb-4 fade-in">
                                {alertMessage}
                            </Alert>
                        )}

                        <div className="contact-form-container glass-effect">
                            <Form onSubmit={handleSubmit}>
                                {/* Honeypot anti-bot Web3Forms: nascosto all'utente */}
                                <div style={{ display: 'none' }} aria-hidden="true">
                                    <Form.Control
                                        type="text"
                                        name="botcheck"
                                        value={formData.botcheck}
                                        onChange={handleChange}
                                        tabIndex="-1"
                                        autoComplete="off"
                                    />
                                </div>

                                <Row>
                                    <Col md={6} className="mb-3">
                                        <Form.Group>
                                            <Form.Label>Nome *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="custom-input"
                                                placeholder="Il tuo nome"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Form.Group>
                                            <Form.Label>Email *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="custom-input"
                                                placeholder="la-tua-email@esempio.com"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6} className="mb-3">
                                        <Form.Group>
                                            <Form.Label>Azienda</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                className="custom-input"
                                                placeholder="Nome della tua azienda"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Form.Group>
                                            <Form.Label>Telefono</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="custom-input"
                                                placeholder="Il tuo numero di telefono"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Servizio di interesse</Form.Label>
                                    <Form.Select
                                        name="service"
                                        value={formData.service}
                                        onChange={handleChange}
                                        className="custom-input"
                                    >
                                        <option value="">Seleziona un servizio...</option>
                                        <option value="odoo">Gestionale Odoo</option>
                                        <option value="magento">Magento 2 / OpenMage</option>
                                        <option value="backup">Backup Cloud</option>
                                        <option value="filemanager">File Manager</option>
                                        <option value="consulenza">Consulenza Digitale</option>
                                        <option value="pacchetto">Pacchetto Personalizzato</option>
                                        <option value="altro">Altro</option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Messaggio *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        className="custom-input"
                                        placeholder="Raccontaci del tuo progetto..."
                                    />
                                </Form.Group>

                                <div className="text-center">
                                    <Button
                                        type="submit"
                                        className="btn-gradient btn-lg"
                                        disabled={isSubmitting}
                                    >
                                        <span>
                                            {isSubmitting ? 'Invio in corso...' : 'Invia Messaggio'}
                                        </span>
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    </Col>

                    <Col lg={6} className="mb-5">
                        <div className="map-container glass-effect">
                            <h4 className="text-center mb-4" style={{color: '#333'}}>Dove Siamo</h4>
                            <div className="map-wrapper" style={{height: '350px', borderRadius: '15px', overflow: 'hidden'}}>
                                <MapContainer
                                    center={[45.6665, 8.9981]}
                                    zoom={15}
                                    style={{height: '100%', width: '100%'}}
                                >
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    />
                                    <Marker position={[45.6665, 8.9981]}>
                                        <Popup>
                                            BearSoftware<br />
                                            Via Isonzo 6/B<br />
                                            22078 Turate (CO), Italia
                                        </Popup>
                                    </Marker>
                                </MapContainer>
                            </div>
                            <div className="location-info mt-3 text-center">
                                <p style={{color: '#333', margin: 0}}>
                                    <i className="fas fa-map-marker-alt" style={{color: 'var(--bear-orange)', marginRight: '8px'}}></i>
                                    Via Isonzo 6/B 22078 Turate (CO), Italia
                                </p>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row className="mt-4">
                    <Col lg={4} className="text-center mb-4">
                        <div className="contact-info">
                            <div className="contact-icon"><i className="fas fa-envelope"></i></div>
                            <h5 className="text-white">Email</h5>
                            <p className="text-white-50">info@bearsoftware.it</p>
                        </div>
                    </Col>
                    <Col lg={4} className="text-center mb-4">
                        <div className="contact-info">
                            <div className="contact-icon"><i className="fas fa-phone"></i></div>
                            <h5 className="text-white">Telefono</h5>
                            <p className="text-white-50">0245073413</p>
                        </div>
                    </Col>
                    <Col lg={4} className="text-center mb-4">
                        <div className="contact-info">
                            <div className="contact-icon"><i className="fas fa-clock"></i></div>
                            <h5 className="text-white">Orari</h5>
                            <p className="text-white-50">Lun-Ven: 9:00-18:00</p>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

export default ContactSection;
